import { isStudioDesktop } from '@repo/studio-desktop-bridge/runtime'
import { useCoinBalance } from '@repo/studio-hooks-coins/use-coin-balance'
import { userQueryOptions, useUserQuery } from 'auth/hooks'

const COIN_THRESHOLD = 10_000

/**
 * Drives the generic "Add 1,000,000 coins" top-banner that pitches a one-time coin top-up
 * (see `LowCoinsBanner` -> `usePurchaseCoins` with `CoinPack1000k`). It is shown to any
 * Studio AI user (not in trial) whose balance is below {@link COIN_THRESHOLD}, regardless of
 * whether they already have a coin subscription, and never in Studio Desktop. This banner
 * predates STU-7650.
 *
 * Precedence with the STU-7650 coin-subscription banner is handled in `StudioBanners`: the
 * subscription banner takes the top slot for non-subscribed Studio AI users until they
 * dismiss it, after which we intentionally fall through to this top-up banner so users still
 * have a path to add coins.
 */
export function useLowCoinsBannerQuery() {
  const { data: coinBalance, isPending: isCoinsPending } = useCoinBalance()
  const { data: hasActiveSubscription, isPending: isApplicableUserPending } =
    useHasActiveAIStudioSubscriptionNotInTrialQuery()

  const isPending = isCoinsPending || isApplicableUserPending
  const isLowOnCoins = coinBalance !== undefined && coinBalance < COIN_THRESHOLD
  const shouldShow = hasActiveSubscription === true && isLowOnCoins && !isStudioDesktop()

  return { isPending, data: shouldShow }
}

function useHasActiveAIStudioSubscriptionNotInTrialQuery() {
  return useUserQuery(undefined, {
    ...userQueryOptions,
    select(data) {
      const studioAi = data.me?.user?.subscriptionAccess?.studioAi
      return studioAi?.isSubscribed === true && studioAi?.subscription?.isTrial === false
    },
  })
}
